import { useEffect, useState, useRef, useCallback } from 'react'
import { useChatStore } from '../../stores/useChatStore.js'
import { useAuthStore } from '../../stores/useAuthStore.js'
import { apiFetch } from '../../lib/api-client.js'
import { Spinner, EmptyState } from '../../components/index.js'
import { ConversationCard } from './ConversationCard.js'
import type { Chat } from '../../types/chat.js'
import type { UserProfile } from '../../types/user.js'

interface ConversationListProps {
  users: UserProfile[]
  onNewChat: () => void
  onCreateGroup: () => void
  onSelectChat: (chatId: string) => void
}

export function ConversationList({ users, onNewChat, onCreateGroup, onSelectChat }: ConversationListProps) {
  const chats = useChatStore((s) => s.chats)
  const activeChat = useChatStore((s) => s.activeChat)
  const setChats = useChatStore((s) => s.setChats)
  const isLoading = useChatStore((s) => s.isLoadingChats)
  const setLoading = useChatStore((s) => s.setLoadingChats)
  const messages = useChatStore((s) => s.messages)
  const pinnedChatIds = useChatStore((s) => s.pinnedChatIds)
  const togglePin = useChatStore((s) => s.togglePin)
  const removeChat = useChatStore((s) => s.removeChat)
  const user = useAuthStore((s) => s.user)!

  const [search, setSearch] = useState('')
  const [showAdminMenu, setShowAdminMenu] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<{ chatId: string; name: string } | null>(null)
  const [deleteInput, setDeleteInput] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const adminMenuRef = useRef<HTMLDivElement>(null)

  const isAdmin = user.role === 'administrator'

  useEffect(() => {
    setLoading(true)
    apiFetch<Chat[]>('/api/internal-chats')
      .then(setChats)
      .catch((err) => console.error('[ChatInterno] Erro ao listar conversas:', err))
      .finally(() => setLoading(false))
  }, [setChats, setLoading])

  // Close admin menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (adminMenuRef.current && !adminMenuRef.current.contains(e.target as Node)) {
        setShowAdminMenu(false)
      }
    }
    if (showAdminMenu) {
      document.addEventListener('mousedown', handleClick)
      return () => document.removeEventListener('mousedown', handleClick)
    }
  }, [showAdminMenu])

  function getLastMessagePreview(chatId: string): string | undefined {
    const msgs = messages[chatId]
    if (!msgs || msgs.length === 0) return undefined
    const last = msgs[msgs.length - 1]
    return last.content ?? (last.attachments?.length ? 'Anexo' : undefined)
  }

  function getChatDisplayName(chat: Chat): string {
    if (chat.type === 'group') return chat.name ?? 'Grupo sem nome'
    const otherMember = chat.members?.find((m) => m.userId !== user.id)
    if (otherMember) {
      const otherUser = users.find((u) => u.id === otherMember.userId)
      if (otherUser) return otherUser.name
    }
    return chat.name ?? 'Conversa direta'
  }

  // Filter chats by search
  const filteredChats = search
    ? chats.filter((c) => getChatDisplayName(c).toLowerCase().includes(search.toLowerCase()))
    : chats

  // Separate pinned and unpinned
  const pinnedChats = filteredChats.filter((c) => pinnedChatIds.includes(c.id))
  const unpinnedChats = filteredChats.filter((c) => !pinnedChatIds.includes(c.id))

  // Sort pinned by pin order
  pinnedChats.sort((a, b) => pinnedChatIds.indexOf(a.id) - pinnedChatIds.indexOf(b.id))

  const handleDeleteGroup = useCallback(async () => {
    if (!deleteConfirm || isDeleting) return
    setIsDeleting(true)
    try {
      await apiFetch(`/api/internal-chats/${deleteConfirm.chatId}`, { method: 'DELETE' })
      removeChat(deleteConfirm.chatId)
      setDeleteConfirm(null)
      setDeleteInput('')
    } catch (err) {
      console.error('[ChatInterno] Erro ao excluir grupo:', err)
    } finally {
      setIsDeleting(false)
    }
  }, [deleteConfirm, isDeleting, removeChat])

  const groups = chats.filter((c) => c.type === 'group')

  return (
    <div className="flex h-full flex-col border-r border-(--color-border) bg-(--color-bg-secondary)">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-(--color-border) px-4 py-3">
        <h2 className="text-sm font-semibold text-(--color-text-primary)">Conversas</h2>
        <div className="flex items-center gap-1">
          {/* New direct conversation */}
          <button
            onClick={onNewChat}
            className="flex h-8 w-8 items-center justify-center rounded-md text-(--color-primary) hover:bg-(--color-bg-tertiary) transition-colors"
            title="Nova conversa"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>

          {/* Admin menu */}
          {isAdmin && (
            <div className="relative" ref={adminMenuRef}>
              <button
                onClick={() => setShowAdminMenu((prev) => !prev)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-(--color-text-muted) hover:bg-(--color-bg-tertiary) hover:text-(--color-text-primary) transition-colors"
                title="Mais opções"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
              </button>

              {showAdminMenu && (
                <div className="absolute right-0 top-full z-50 mt-1 w-52 rounded-lg border border-(--color-border) bg-(--color-bg-primary) py-1 shadow-lg">
                  <button
                    onClick={() => {
                      setShowAdminMenu(false)
                      onCreateGroup()
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-(--color-text-primary) hover:bg-(--color-bg-tertiary) transition-colors"
                  >
                    <svg className="h-4 w-4 text-(--color-text-muted)" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                    </svg>
                    Criar grupo
                  </button>

                  {groups.length > 0 && (
                    <>
                      <div className="my-1 border-t border-(--color-border)" />
                      <p className="px-3 py-1 text-[10px] font-medium uppercase text-(--color-text-muted)">Excluir grupo</p>
                      {groups.map((g) => (
                        <button
                          key={g.id}
                          onClick={() => {
                            setShowAdminMenu(false)
                            setDeleteConfirm({ chatId: g.id, name: g.name ?? 'Grupo' })
                          }}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-(--color-danger) hover:bg-(--color-danger)/5 transition-colors"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                          {g.name ?? 'Grupo'}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="px-3 py-2">
        <div className="relative">
          <svg className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-(--color-text-muted)" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar conversas..."
            className="w-full rounded-lg border border-(--color-border) bg-(--color-bg-primary) py-1.5 pl-8 pr-8 text-xs text-(--color-text-primary) placeholder:text-(--color-text-muted) focus:border-(--color-primary) focus:outline-none"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-4 w-4 items-center justify-center rounded-full text-(--color-text-muted) hover:text-(--color-text-primary) transition-colors"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Chat list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        ) : filteredChats.length === 0 ? (
          <EmptyState
            title={search ? 'Nenhum resultado' : 'Nenhuma conversa'}
            description={search ? 'Tente outro termo de busca' : 'Inicie uma conversa com um colega'}
          />
        ) : (
          <>
            {/* Pinned section */}
            {pinnedChats.length > 0 && (
              <>
                <div className="flex items-center gap-1.5 px-4 py-1.5">
                  <svg className="h-3 w-3 text-(--color-text-muted)" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                  </svg>
                  <span className="text-[10px] font-medium uppercase text-(--color-text-muted)">Fixadas</span>
                </div>
                {pinnedChats.map((chat) => (
                  <ConversationCard
                    key={chat.id}
                    chat={chat}
                    isActive={activeChat === chat.id}
                    users={users}
                    currentUserId={user.id}
                    lastMessagePreview={getLastMessagePreview(chat.id)}
                    isPinned
                    onTogglePin={() => togglePin(chat.id)}
                    canPin
                    onClick={() => onSelectChat(chat.id)}
                  />
                ))}
                <div className="mx-4 my-1 border-t border-(--color-border)" />
              </>
            )}

            {/* Unpinned chats */}
            {unpinnedChats.map((chat) => (
              <ConversationCard
                key={chat.id}
                chat={chat}
                isActive={activeChat === chat.id}
                users={users}
                currentUserId={user.id}
                lastMessagePreview={getLastMessagePreview(chat.id)}
                isPinned={false}
                onTogglePin={() => togglePin(chat.id)}
                canPin={pinnedChatIds.length < 3}
                onClick={() => onSelectChat(chat.id)}
              />
            ))}
          </>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={() => { setDeleteConfirm(null); setDeleteInput('') }}>
          <div className="mx-4 w-full max-w-sm rounded-xl border border-(--color-border) bg-(--color-bg-primary) p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-(--color-text-primary)">Excluir grupo</h3>
            <p className="mt-2 text-xs text-(--color-text-muted)">
              Esta ação é irreversível. Todas as mensagens serão perdidas.
            </p>
            <p className="mt-3 text-xs text-(--color-text-primary)">
              Digite <strong>{deleteConfirm.name}</strong> para confirmar:
            </p>
            <input
              type="text"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              autoFocus
              className="mt-2 w-full rounded-lg border border-(--color-border) bg-(--color-bg-secondary) px-3 py-2 text-sm text-(--color-text-primary) focus:border-(--color-danger) focus:outline-none"
              placeholder={deleteConfirm.name}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => { setDeleteConfirm(null); setDeleteInput('') }}
                className="rounded-lg px-3 py-1.5 text-sm text-(--color-text-muted) hover:bg-(--color-bg-tertiary) transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteGroup}
                disabled={deleteInput !== deleteConfirm.name || isDeleting}
                className="rounded-lg bg-(--color-danger) px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-(--color-danger)/90 disabled:opacity-50"
              >
                {isDeleting ? 'Excluindo...' : 'Excluir grupo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
